import { useState } from 'react';
import type { Evento } from '../../api/ops/eventos';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Desplegable } from '../../componentes/Desplegable';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { PanelLateral } from '../../componentes/PanelLateral';
import { avisoBreveDeError } from '../../logica/estados';
import type { PropsEventos } from './Eventos';
import { Ceja, CampoDiaHora } from './piezas';
import { erroresNuevo, type ValoresNuevo } from './formulario';
import { deCampo, erroresDeCampo, MODO, MODOS, zonaLocal } from './reglas';

/**
 * Nuevo evento (10.2): nombre, sede, empieza y termina (obligatorio: así se cierran la venta y las listas), cómo se entra,
 * aforo y cupo de taquilla (≤ aforo, G4). Se crea en borrador con `createEvent`; luego se abre su ficha.
 */
export function NuevoEvento({ api, negocio, movil, membresia, sedes, onCerrar }: PropsEventos & { sedes: readonly { id: string; nombre: string }[]; onCerrar: (creado?: Evento) => void }) {
    const avisar = useAvisoBreve();
    const zona = membresia?.timezone ?? zonaLocal();
    const [v, setV] = useState<ValoresNuevo>({ nombre: '', sede: sedes[0]?.id ?? '', inicio: '', fin: '', modo: 'tickets', aforo: '', cupo: '0' });
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [deApi, setDeApi] = useState<Record<string, string>>({});
    const errores = { ...(intentado ? erroresNuevo(v, zona) : {}), ...deApi };
    const cambia = <K extends keyof ValoresNuevo>(k: K) => (valor: ValoresNuevo[K]) => {
        setV((x) => ({ ...x, [k]: valor }));
        setDeApi({});
    };

    const crear = () => {
        setIntentado(true);
        if (Object.keys(erroresNuevo(v, zona)).length > 0) {
            return;
        }
        setOcupado(true);
        api.createEvent(negocio, {
            name: v.nombre.trim(),
            location_id: v.sede,
            starts_at: deCampo(v.inicio, zona) ?? '',
            ends_at: deCampo(v.fin, zona) ?? '',
            sale_mode: v.modo,
            capacity: Number(v.aforo),
            box_office_quota: v.cupo.trim() === '' ? 0 : Number(v.cupo),
        })
            .then((r) => {
                avisar({ tipo: 'ok', texto: 'Evento creado en borrador.' });
                onCerrar(r.datos);
            })
            .catch((e: unknown) => {
                setOcupado(false);
                const campos = erroresDeCampo(e);
                setDeApi(campos);
                if (Object.keys(campos).length === 0) {
                    avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido crear el evento. Prueba de nuevo.', reintentar: crear });
                }
            });
    };

    return (
        <PanelLateral abierto onCerrar={() => { onCerrar(); }} movil={movil} titulo="Crear un evento" eyebrow="Nuevo evento" subtitulo="Se crea en borrador." icono="ticket" accion="Crear borrador" ocupado={ocupado} ocupadoTexto="Creando…" onAccion={crear} cerrar="Cancelar">
            <div className="rv-ev-form">
                <Campo etiqueta="Nombre del evento" valor={v.nombre} onChange={cambia('nombre')} error={errores.name} ayuda="Como lo verán tus clientes." max={120} />
                <Desplegable etiqueta="Sede" valor={v.sede} opciones={sedes.map((s) => ({ valor: s.id, texto: s.nombre }))} onChange={cambia('sede')} error={errores.location_id} />
                <CampoDiaHora etiqueta="Empieza" valor={v.inicio} onChange={cambia('inicio')} error={errores.starts_at} />
                <CampoDiaHora etiqueta="Termina" valor={v.fin} onChange={cambia('fin')} error={errores.ends_at} ayuda="Obligatorio: así se cierran la venta y las listas." />
                <Ceja>Cómo se entra</Ceja>
                <div className="rv-ev-opciones" role="radiogroup" aria-label="Cómo se entra">
                    {MODOS.map((m) => (
                        <OpcionRadio key={m} elegida={v.modo === m} onElegir={() => { cambia('modo')(m); }} titulo={MODO[m][0]} texto={MODO[m][1]} />
                    ))}
                </div>
                <div className="rv-ca-dos">
                    <Campo etiqueta="Aforo" tipo="number" modo="numeric" valor={v.aforo} onChange={cambia('aforo')} error={errores.capacity} />
                    <Campo etiqueta="Cupo de taquilla" tipo="number" modo="numeric" valor={v.cupo} onChange={cambia('cupo')} error={errores.box_office_quota} ayuda="Para la puerta, también sin conexión." />
                </div>
            </div>
        </PanelLateral>
    );
}
